import useAuthState from '@/states/auth.state';
import useTenantState from '@/states/tenant.state';
import { axiosInstance } from '@/utils/lib/axios';
import { useQueryClient } from '@tanstack/react-query';
import { Spinner } from 'alurkerja-ui';
import React, { useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

type ProtectedRouteProps = {
  layout: React.FC;
};

export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ layout: Layout }) => {
  const navigate = useNavigate();
  const { tenant_id } = useParams();
  const {
    token,
    tenants,
    loadUserProfile,
    isLogoutLoading,
    isSuperAdmin,
    allTenants,
    isLoadingAllTenants,
    loadAllTenants,
  } = useAuthState();
  const { setCurrentTenant, currentTenant } = useTenantState();
  const queryClient = useQueryClient();
  const [isProfileChecked, setIsProfileChecked] = React.useState(false);

  useEffect(() => {
    // Setup Axios interceptor immediately
    const interceptorId = axiosInstance.interceptors.request.use(
      config => {
        const state = useAuthState.getState();
        const tenantState = useTenantState.getState();
        const activeToken = state.token;
        const activeTenant = tenantState.currentTenant;

        if (activeToken) {
          config.headers.Authorization = `Bearer ${activeToken}`;
        }

        if (activeTenant) {
          config.headers['X-ACTIVE-TENANT'] = activeTenant.slug;
        }

        config.headers['Content-Type'] = 'application/json';

        return config;
      },
      error => {
        return Promise.reject(error);
      },
    );

    const initializeUserProfile = async () => {
      // Use the token from the hook, which is reactive
      if (token) {
        try {
          await loadUserProfile(token, true);
        } catch (error) {
          console.error('Failed to load user profile:', error);
        } finally {
          setIsProfileChecked(true);
        }
      } else {
        setIsProfileChecked(true);
      }
    };

    initializeUserProfile();

    return () => {
      axiosInstance.interceptors.request.eject(interceptorId);
    };
  }, [token, loadUserProfile]);

  useEffect(() => {
    if (token && isSuperAdmin && !allTenants) {
      loadAllTenants();
    }
  }, [token, isSuperAdmin, allTenants, loadAllTenants]);

  useEffect(() => {
    if (!token) {
      navigate(`/login?tenantId=${tenant_id}&redirect_to=${encodeURIComponent(location.pathname)}`);
      return;
    }

    if (!isProfileChecked) {
      return;
    }

    // Redirect to verify-email if email is not verified
    const userProfile = useAuthState.getState().userProfile;
    if (userProfile && !userProfile.email_verified_at) {
      navigate('/verify-email');
      return;
    }

    if (!tenant_id || ['undefined', 'null', ''].includes(tenant_id)) {
      console.error('Tenant ID is not defined in URL');
      navigate(`/workspaces`);
      return;
    }

    // For superadmin: wait for allTenants to load BEFORE checking if tenant exists
    // This prevents premature redirect to /workspaces
    if (isSuperAdmin && (!allTenants || isLoadingAllTenants)) {
      console.info('Superadmin: waiting for all tenants to load...');
      return;
    }

    // For regular users: wait for tenants to load
    if (!isSuperAdmin && !tenants) {
      console.info('Tenants not loaded yet, waiting...');
      return;
    }

    const availableTenants = isSuperAdmin && allTenants ? allTenants : tenants;

    const tenantExists = availableTenants ? availableTenants.filter(t => t.slug === tenant_id).length > 0 : false;

    if (!tenantExists) {
      console.error(`Tenant with ID "${tenant_id}" not found`);
      setCurrentTenant(null);
      navigate(`/workspaces`);
      return;
    }

    if (currentTenant?.slug !== tenant_id) {
      console.info(
        `Syncing state: URL tenant is "${tenant_id}", state tenant was "${currentTenant?.slug}". Updating...`,
      );
      const tenant = availableTenants?.find(t => t.slug === tenant_id);
      setCurrentTenant(tenant!);
      queryClient.invalidateQueries();
    } else if (currentTenant && availableTenants) {
      const freshTenant = availableTenants.find(t => t.slug === tenant_id);
      if (freshTenant && JSON.stringify(currentTenant.roles) !== JSON.stringify(freshTenant.roles)) {
        setCurrentTenant(freshTenant);
      }
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    tenant_id,
    token,
    currentTenant,
    tenants,
    allTenants,
    isSuperAdmin,
    isLoadingAllTenants,
    setCurrentTenant,
    isProfileChecked,
  ]);

  if (!token) {
    return (
      <div className="fixed flex items-center justify-center w-screen h-screen gap-2">
        <Spinner className="text-primary" />
        Loading...
      </div>
    );
  }

  if (isLogoutLoading) {
    return (
      <div className="fixed flex items-center justify-center w-screen h-screen gap-2">
        <Spinner className="text-primary" />
        Logout...
      </div>
    );
  }

  if (!isProfileChecked) {
    return (
      <div className="fixed flex items-center justify-center w-screen h-screen gap-2">
        <Spinner className="text-primary" />
        Loading Profile...
      </div>
    );
  }

  if (isSuperAdmin && isLoadingAllTenants) {
    return (
      <div className="fixed flex items-center justify-center w-screen h-screen gap-2">
        <Spinner className="text-primary" />
        Loading all workspaces for super admin...
      </div>
    );
  }

  return <Layout />;
};
